import React, { useState, useEffect } from 'react';
import { useSearchParams, useParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Filter, DollarSign } from 'lucide-react';
import { Sidebar } from './ui/Sidebar';
import { ProductList } from './ProductList';
import { MobileFilters } from './ui/MobileFilters';

// Tag labels mapping
const TAG_LABELS = {
  best_seller: 'Best Seller',
  new_release: 'New Releases',
  prime: 'Prime Deals',
  hot_deal: 'Hot Deals',
  price_drop: 'Price Drops',
  trending: 'Trending',
  most_wished: 'Most Wished For',
  gift_idea: 'Gift Ideas',
};

// Normalize price to nearest bracket for canonical URLs
function normalizePrice(price, bracket = 50) {
  if (!price) return null;
  return Math.round(price / bracket) * bracket;
}

export function FilterPage() {
  const [searchParams] = useSearchParams();
  const { tagSlug, categorySlug, subSlug } = useParams();
  const location = useLocation();
  const [view, setView] = useState('grid');
  
  // Parse URL params for price filters
  const minPrice = searchParams.get('min') || searchParams.get('low-price') || '';
  const maxPrice = searchParams.get('max') || searchParams.get('high-price') || '';

  // Build filter state based on all URL params
  const [filters, setFilters] = useState({
    search: subSlug || '',
    category: categorySlug || 'all',
    productTags: tagSlug ? [tagSlug] : [],
    minPrice: minPrice,
    maxPrice: maxPrice,
    stockStatus: '',
    sort: 'featured'
  });

  // Update filters when URL params change
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      search: subSlug || '',
      category: categorySlug || 'all',
      productTags: tagSlug ? [tagSlug] : [],
      minPrice: minPrice,
      maxPrice: maxPrice
    }));
  }, [tagSlug, categorySlug, subSlug, minPrice, maxPrice]);

  // Build human-readable text for SEO
  const readableTag = tagSlug ? (TAG_LABELS[tagSlug] || tagSlug.replace(/-/g, ' ')) : '';
  const readableCategory = categorySlug ? categorySlug.replace(/-/g, ' ') : '';
  const readableSub = subSlug ? subSlug.replace(/-/g, ' ') : '';

  const priceRangeText = minPrice && maxPrice 
    ? `$${minPrice} - $${maxPrice}`
    : minPrice 
    ? `over $${minPrice}`
    : maxPrice 
    ? `under $${maxPrice}`
    : '';

  // Build dynamic title
  const titleParts = [
    readableTag,
    readableSub,
    readableCategory,
    priceRangeText
  ].filter(Boolean);

  const title = titleParts.length > 0
    ? `${titleParts.join(' ')} - Best Deals & Reviews | TechieDay`
    : 'Filter Products - Best Tech Deals | TechieDay';

  const description = titleParts.length > 0
    ? `Compare and find the best ${titleParts.join(' ').toLowerCase()} with latest reviews, prices, and ratings updated daily on TechieDay.`
    : `Browse tech products filtered by price range ${priceRangeText}. Find the best deals on computers, electronics, and tech accessories within your budget.`;

  // Build canonical URL with normalized prices
  const canonicalMin = normalizePrice(minPrice);
  const canonicalMax = normalizePrice(maxPrice);
  const canonicalQuery = [];
  if (canonicalMin) canonicalQuery.push(`min=${canonicalMin}`);
  if (canonicalMax) canonicalQuery.push(`max=${canonicalMax}`);
  const canonicalURL = `${location.pathname}${canonicalQuery.length > 0 ? '?' + canonicalQuery.join('&') : ''}`;

  // Build H1 heading
  const h1Text = titleParts.length > 0 
    ? titleParts.join(' ')
    : `Filtered Products ${priceRangeText}`;

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://www.techieday.com${canonicalURL}`} />
        <meta name="keywords" content={`${titleParts.join(', ')}, tech deals, reviews, best prices`} />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
        <header className="bg-gradient-to-r from-green-600 to-teal-600 text-white">
          <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="text-center">
              <div className="flex items-center justify-center mb-4">
                <Filter className="w-8 h-8 mr-3" />
                <h1 className="text-2xl font-semibold text-white">{h1Text}</h1>
              </div>
              <p className="text-lg opacity-90 max-w-3xl mx-auto">
                {description}
              </p>
              {priceRangeText && (
                <div className="mt-4 inline-flex items-center text-sm opacity-75">
                  <DollarSign className="w-4 h-4 mr-2" />
                  Price Range: {priceRangeText}
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar */}
            <aside className="hidden lg:block w-64 flex-shrink-0">
              <Sidebar filters={filters} onFilterChange={setFilters} />
            </aside>

            {/* Product List */}
            <div className="flex-1">
              <ProductList filters={filters} view={view} />
            </div>
          </div>
        </main>

        <MobileFilters filters={filters} onFilterChange={setFilters} />
      </div>
    </>
  );
}
